<script lang="ts" module>
  import { reactiveContext, type Defaults } from "#lib/internal/helpers"

  export type CollapsibleRootProps = BaseProps & {
    /**
     * The controlled open state of the collapsible.
     */
    open?: boolean
    /**
     * When `true`, prevents the user from interacting with the collapsible.
     */
    disabled?: boolean
  }

  type RootContext = {
    open: boolean
    readonly disabled: boolean
  }

  const defaults = {
    open: false,
    disabled: false,
  } satisfies Defaults<RootContext>

  const { getContext, setContext } = reactiveContext<RootContext>(defaults)
  export const getCollapsibleRootContext = getContext
</script>

<script lang="ts">
  import { useActions } from "#lib/internal/helpers/useActions"
  import type { BaseProps } from "#lib/internal/types"
  import type { Snippet } from "svelte"

  type $$Props = CollapsibleRootProps
  interface Props {
    open?: $$Props["open"]
    disabled?: $$Props["disabled"]
    use?: $$Props["use"]
    children?: Snippet
    [key: string]: any
  }

  let {
    open = $bindable(defaults.open),
    disabled = defaults.disabled,
    use = [],
    children,
    ...rest
  }: Props = $props()

  const ctx = setContext({
    open: (v) => {
      if (v !== open) open = v
    },
  })
  $effect(() => {
    ctx.set({ open, disabled })
  })
</script>

<div
  data-state={open ? "open" : "closed"}
  data-disabled={disabled ? "true" : undefined}
  use:useActions={use ?? []}
  {...rest}
>
  {@render children?.()}
</div>
